/**
 * English — secondary language.
 *
 * Typed as ``FaResources`` so the key set can never drift from Persian: a missing
 * or misspelled key is a TypeScript error (PROMPT.md section 14.5).
 * Persian remains the default and the fallback language.
 */
import type { FaResources } from "./fa";

export const en: FaResources = {
  common: {
    appName: "Xerex AI",
    panel: "Admin Panel",
    loading: "Loading…",
    saving: "Saving…",
    noData: "No data to display",
    notAvailable: "Not available",
    unknown: "Unknown",
    never: "Never",
    yes: "Yes",
    no: "No",
    all: "All",
    none: "None",
    optional: "Optional",
    required: "Required",
    refreshing: "Refreshing…",
    actions: { save: "Save changes", cancel: "Cancel", delete: "Delete", edit: "Edit", details: "Details", add: "Add", search: "Search", filter: "Filter", export: "Export", retry: "Retry", refresh: "Refresh", copy: "Copy", copied: "Copied", close: "Close", confirm: "Confirm", back: "Back", showMore: "Show more", showLess: "Show less", reset: "Reset", apply: "Apply", submit: "Submit", view: "View", download: "Download", enable: "Enable", disable: "Disable", select: "Select", copyFailed: "Copy failed" },
    status: { label: "Status", enabled: "Enabled", disabled: "Disabled", active: "Active", inactive: "Inactive", pending: "Pending" },
    table: { empty: "No records found", loading: "Fetching data…", rowsPerPage: "Rows per page", page: "Page", of: "of", total: "Total", showingRange: "Showing {{from}} to {{to}} of {{total}} records", first: "First", previous: "Previous", next: "Next", last: "Last" },
    search: { placeholder: "Search…", noResults: "No results for “{{query}}”" },
    confirmDialog: { title: "Confirm action", defaultMessage: "Are you sure you want to continue?", irreversible: "This action cannot be undone." },
    toast: { success: "Operation completed", error: "Operation failed", copied: "Copied to clipboard" },
    time: { justNow: "just now", secondsAgo: "{{count}} seconds ago", minutesAgo: "{{count}} minutes ago", hoursAgo: "{{count}} hours ago", daysAgo: "{{count}} days ago", never: "never" },
    units: { ms: "ms", seconds: "s", requests: "requests", tokens: "tokens", providers: "providers", models: "models", keys: "keys", percent: "%" },
    technical: { label: "Technical", copyValue: "Copy value", showRaw: "Show raw data", rawData: "Raw data", identifiers: "Technical identifiers" },
    language: { label: "Language", fa: "فارسی", en: "English", switchTo: "Switch language to {{language}}" },
    theme: { label: "Theme", light: "Light", dark: "Dark", system: "System" },
    build: { version: "Version", milestone: "Milestone", environment: "Environment", serverTime: "Server time", milestoneValue: "Milestone {{milestone}}", footer: "Xerex AI — milestone {{milestone}} · version {{version}}" },
  },

  nav: {
    sectionOverview: "Overview",
    sectionGateway: "AI gateway",
    sectionInsights: "Monitoring & usage",
    sectionAdministration: "Administration",
    dashboard: "Dashboard",
    providers: "Providers",
    credentials: "Credentials",
    models: "Models",
    apiKeys: "API keys",
    health: "System health",
    routing: "Routing",
    usage: "Usage",
    logs: "Logs",
    users: "Users",
    auditLogs: "Audit log",
    settings: "Settings",
    benchmark: "Benchmark",
    tools: "Tools",
    toolExecutions: "Tool executions",
    collapse: "Collapse sidebar",
    expand: "Expand sidebar",
    openMenu: "Open navigation menu",
    home: "Home",
  },

  layout: {
    breadcrumbHome: "Dashboard",
    userMenu: { profile: "Account", role: "Role", signOut: "Sign out", signedInAs: "Signed in as" },
    environmentNotice: "{{environment}} environment — evaluation build.",
    notices: { title: "Notifications", empty: "No new notifications" },
  },

  auth: {
    signInTitle: "Sign in to the admin panel",
    signInSubtitle: "Manage providers, models and routing.",
    email: "Email",
    emailPlaceholder: "name@example.com",
    password: "Password",
    passwordPlaceholder: "Enter your password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    signOut: "Sign out",
    bootstrapTitle: "Initial setup",
    bootstrapSubtitle: "Create the first owner account to start managing the platform.",
    bootstrapSubmit: "Create owner account",
    bootstrapping: "Creating account…",
    fullName: "Full name",
    fullNamePlaceholder: "e.g. Sara Mohammadi",
    forgotPassword: "Forgot your password?",
    forgotPasswordHint: "Password recovery arrives in a later milestone.",
    securityNote: "Your session uses secure tokens and an HttpOnly cookie.",
    bootstrapDisabledNotice: "First-run setup is disabled on this deployment. Ask the platform owner to create the first administrator account.",
    sessionExpired: "Your session expired. Please sign in again.",
    signedInAs: "Signed in as {{email}}",
    passwordHint: "At least 10 characters, mixing letters and digits.",
  },

  dashboard: {
    title: "Dashboard",
    subtitle: "Platform state over the last {{window}}.",
    windowLabel: "Window",
    sectionKpi: "Key indicators",
    sectionProviderHealth: "Provider status",
    sectionTopModels: "Top models",
    sectionModelPerformance: "Model performance report",
    sectionPlatformState: "Platform setup state",
    modelPerf: {
      model: "Model",
      provider: "Provider",
      samples: "Samples",
      successRate: "Success",
      latency: "Latency",
      timeoutRate: "Timeouts",
      probe: "Probe",
      responding: "Responding",
      score: "Score",
      yes: "yes",
      no: "no",
      coldStart: "cold start",
      empty: "No enabled models yet",
      emptyHint: "Add a provider and discover models to see the performance report.",
      weightsNote: "Weights from Settings → Smart router scoring.",
      windowNote: "Window: {{window}}",
    },
    kpi: {
      requests: "Requests",
      tokens: "Token usage",
      cost: "Cost",
      p95Latency: "p95 latency",
      errorRate: "Error rate",
      activeProviders: "Active providers",
      noSamples: "no samples",
    },
    empty: {
      providers: "No providers configured yet",
      providersHint: "Add a provider in the next milestone to see real statistics.",
      models: "No models registered",
      modelsHint: "After adding a provider, run “Discover models” from the Models screen.",
      usage: "No traffic recorded",
      usageHint: "Usage statistics appear after the first request reaches the AI gateway.",
      noModels: "No models with performance data yet",
      noModelsHint: "Probe runs hourly; traffic stats appear after real requests.",
    },
    table: {
      rank: "#",
      model: "Model",
      provider: "Provider",
      successRate: "Success",
      latency: "Avg latency",
      timeoutRate: "Timeouts",
      probe: "Probe",
      score: "Score",
      responding: "Responding",
      samples: "Samples",
    },
    platform: {
      adminUsers: "Administrator accounts",
      apiKeys: "Active API keys",
      providers: "Registered providers",
      models: "Registered models",
    },
    honestyNote: "Every number on this screen is read from the real database; until a provider is connected the values are zero.",
  },

  health: {
    title: "System health",
    subtitle: "Live checks for the database, Redis and the API runtime.",
    overall: "Overall status",
    components: "Components",
    component: "Component",
    componentNames: { database: "Database", redis: "Redis", api: "API service" },
    latency: "Latency",
    version: "Version",
    dialect: "Dialect",
    lastCheck: "Last check",
    checkedAt: "Checked at",
    checkNow: "Run health check",
    checking: "Checking…",
    required: "Required",
    optional: "Optional",
    degradedHint: "Redis is unreachable. The platform runs in degraded mode and rate limiting is bypassed.",
    downtimeHint: "A required component is down; administrative requests may fail.",
    runtime: "Uptime",
    serviceInfo: "Service details",
    milestones: { milestone: "Milestone", environment: "Environment", startedAt: "Started at" },
    monitoringNote: "Continuous provider, model and endpoint monitoring is delivered in M5.",
    providers: {
      title: "Provider health",
      subtitle: "The last observation per provider: latency, error rate and 24-hour uptime.",
      errorRate: "Error rate",
      requests: "Requests (24h)",
      empty: "No provider registered yet",
      emptyHint: "Once a provider exists, its check results appear here.",
    },
    run: {
      button: "Run checks",
      done: "Checked {{checked}} providers",
      withFailures: "{{failed}} need attention",
    },
    history: {
      title: "Check history",
      subtitle: "Each row is a stored observation; the values are never adjusted.",
      filter: "Filter by provider",
      allProviders: "All providers",
      empty: "No check recorded yet",
      emptyHint: "Use “Run checks” to record the first observations.",
    },
    uptime: "Uptime (24h)",
  },

  users: {
    title: "Users",
    subtitle: "Manage administrator accounts and access roles.",
    addUser: "Add user",
    createTitle: "New administrator",
    createSubtitle: "Create an account for a colleague and assign the right role.",
    tableEmail: "Email",
    tableName: "Full name",
    tableRole: "Role",
    tableStatus: "Status",
    tableLastLogin: "Last sign-in",
    tableCreatedAt: "Created",
    tableActions: "Actions",
    roleLabel: "Role",
    statusLabel: "Status",
    createdAt: "Created",
    lastLoginAt: "Last sign-in",
    mfaEnabled: "Two-factor authentication",
    editTitle: "Edit user",
    updated: "User changes saved",
    created: "Administrator created",
    passwordLabel: "Initial password",
    passwordHint: "At least 10 characters.",
    roles: { owner: "Owner", admin: "Admin", operator: "Operator", viewer: "Viewer" },
    roleHints: { owner: "Full access to every section and to the team", admin: "Manages providers, models and users", operator: "Operational work such as model discovery and health checks", viewer: "Read-only access" },
    statuses: { active: "Active", suspended: "Suspended", invited: "Invited" },
    selfNote: "You cannot deactivate your own account.",
    empty: "No other users yet",
  },

  auditLogs: {
    title: "Audit log",
    subtitle: "Every administrative change is recorded with a request id.",
    tableTime: "Time",
    tableActor: "Actor",
    tableAction: "Action",
    tableEntity: "Entity",
    tableIp: "IP address",
    tableRequestId: "Request id",
    tableDetails: "Details",
    actions: { login_succeeded: "Successful sign-in", login_failed: "Failed sign-in", logout: "Sign-out", token_refreshed: "Session refreshed", bootstrap_owner_created: "Initial owner created", settings_updated: "Settings changed", user_created: "User created", user_updated: "User updated", user_disabled: "User deactivated", provider_created: "Provider created", provider_updated: "Provider updated", provider_deleted: "Provider deleted", provider_tested: "Provider connection tested", credential_created: "Credential created", credential_updated: "Credential updated", credential_deleted: "Credential deleted", credential_rotated: "Access key rotated", credential_verified: "Access key verified", credentials_exported: "Provider credentials exported", model_created: "Model created", model_updated: "Model updated", model_deleted: "Model deleted", models_discovered: "Models discovered", endpoint_created: "Model endpoint created", endpoint_updated: "Model endpoint updated", endpoint_deleted: "Model endpoint deleted", model_tested: "Model tested" },
    empty: "No audit entries yet",
    detailTitle: "Audit entry details",
  },

  settings: {
    title: "Settings",
    subtitle: "Configure presentation, language and data retention for the panel.",
    sectionBranding: "Branding & Identity",
    sectionPresentation: "Presentation & language",
    sectionNotifications: "Notifications",
    sectionRetention: "Data retention",
    sectionRouter: "Smart router scoring",
    sectionEnvironment: "Environment configuration",
    saveSuccess: "Settings saved",
    saveError: "Settings could not be saved",
    readOnlyBadge: "Set by environment",
    savedAt: "Last change: {{time}}",
    notSavedYet: "No change recorded yet",
    fields: {
      "branding.name": { label: "Assistant name", hint: "Display name shown to users in chat responses." },
      "branding.system_prompt": { label: "System prompt", hint: "Instructions that define the assistant's identity and behavior." },
      "branding.reveal_model": { label: "Reveal model name", hint: "Show the underlying model name in API responses." },
      "ui.default_locale": { label: "Default panel language", hint: "Default language of the panel. Persian is the primary language." },
      "ui.numeral_style": { label: "Numeral style", hint: "Technical values such as identifiers and keys always render with Latin digits." },
      "ui.theme": { label: "Theme", hint: "Light, dark or follow the operating system." },
      "ui.timezone": { label: "Timezone", hint: "Time zone used to display timestamps in the panel." },
      "ui.sidebar_collapsed": { label: "Collapsed sidebar", hint: "Open the sidebar collapsed by default." },
      "notifications.email_enabled": { label: "Email notifications", hint: "Send important platform events by email." },
      "notifications.health_alerts": { label: "Health alerts", hint: "Notify when a provider becomes unhealthy or degraded." },
      "retention.usage_days": { label: "Usage retention (days)", hint: "How long usage records are kept before roll-up and clean-up." },
      "retention.logs_days": { label: "Log retention (days)", hint: "How long request and audit logs are kept." },
      "router.weight_performance": { label: "Performance weight", hint: "How much real traffic success/latency counts when ranking models (0–1)." },
      "router.weight_capability": { label: "Capability weight", hint: "How much static task/complexity matching counts (0–1)." },
      "router.weight_health": { label: "Health bonus scale", hint: "Scales probe/provider health bonuses and penalties (0–1)." },
      "router.weight_timeout": { label: "Timeout penalty", hint: "Penalty multiplier for heavy requests when timeout rate is high (0–1)." },
      "router.perf_success_weight": { label: "Success rate weight", hint: "Share of the performance score from success rate (0–1)." },
      "router.perf_speed_weight": { label: "Speed weight", hint: "Share of the performance score from latency (0–1)." },
    },
    numeralStyles: { persian: "Persian digits (۱۲۳)", latin: "Latin digits (123)" },
    environmentNotice: "These values come from the service environment and cannot be changed from the panel.",
    environmentKeys: { apiPrefix: "API prefix", defaultLocale: "Service default locale", locales: "Supported locales", timezone: "Default timezone" },
  },

  roadmap: {
    badge: "Next milestone",
    title: "This section is not implemented yet",
    description: "To avoid showing incorrect data, this screen ships in milestone {{milestone}}. The planned API contract is listed below.",
    plannedEndpoints: "Planned API contract",
    implementedModules: "Sections available today",
    noFakeData: "This build shows no fabricated data.",
    endpoint: { method: "Method", path: "Path", summary: "Summary", milestone: "Milestone" },
    modules: {
      providers: { title: "Providers", description: "Add a provider, configure base URL, priority, weight, timeout and retries." },
      credentials: { title: "Credentials", description: "Encrypted provider secrets, verification and key rotation." },
      models: { title: "Models", description: "Automatic model discovery, pricing, context window and model testing." },
      apiKeys: { title: "API keys", description: "Issue keys with scopes, quota and rate limits; one-time secret display." },
      routing: { title: "Routing", description: "Priority, weighted, round-robin, latency, cost and failover strategies with simulation." },
      usage: { title: "Usage", description: "Token, cost, latency and error statistics with Jalali date filters." },
      logs: { title: "Logs", description: "Request logs with request id, provider, model and error code." },
      gateway: { title: "AI gateway", description: "OpenAI-compatible endpoints for chat, embeddings and model listing." },
    },
  },

  providers: {
    title: "Providers",
    subtitle: "Manage the AI services connected to the gateway.",
    addProvider: "Add provider",
    exportConfig: {
      button: "Export JSON",
      title: "Export the full configuration",
      message: "Every provider is downloaded as one JSON file including its base URL and access keys.",
      detailSecrets: "The access keys in this file are decrypted and stored as plain text. Keep the file somewhere safe.",
      detailAudit: "Your username, role and IP address are written to the audit trail with this request.",
      confirm: "Download export",
      done: "Downloaded {{filename}}.",
      failed: "The export could not be produced.",
    },
    createTitle: "Add provider",
    createSubtitle: "Enter the connection details. Credentials are stored separately.",
    editTitle: "Edit provider",
    editSubtitle: "Changes are written to the audit trail after saving.",
    empty: "No provider has been registered yet",
    emptyHint: "Add a provider and store its credentials to start discovering models.",
    searchPlaceholder: "Search by name or slug…",
    filterKind: "Kind",
    filterState: "State",
    orderBy: "Order",
    allKinds: "All kinds",
    allStates: "All states",
    sortPriority: "Priority",
    sortName: "Name",
    sortNewest: "Newest",
    clearFilters: "Clear filters",
    table: {
      name: "Name",
      kind: "Kind",
      baseUrl: "Base URL",
      credentials: "Credentials",
      models: "Models",
      health: "Health",
      priority: "Priority",
      state: "State",
      updatedAt: "Last change",
      actions: "Actions",
    },
    test: {
      action: "Test connection",
      running: "Testing…",
      ok: "The connection was established.",
      failed: "The connection could not be established.",
      latency: "Latency",
      statusCode: "Status code",
      models: "Models discovered",
      needCredential: "Store a credential for this provider before testing the connection.",
      title: "Connection test result",
    },
    form: {
      sectionIdentity: "Identity",
      sectionConnection: "Connection",
      sectionRouting: "Routing & resilience",
      name: "Name",
      nameHint: "The name shown in the panel.",
      kind: "Kind",
      kindHint: "The kind determines request shape and authentication scheme.",
      baseUrl: "Base URL",
      baseUrlHint: "API base URL, for example https://api.openai.com/v1",
      slug: "Slug",
      slugHint: "Optional; derived from the name when left empty.",
      description: "Description",
      priority: "Priority",
      priorityHint: "A lower number means a higher priority.",
      weight: "Weight",
      weightHint: "Used by weighted routing.",
      timeout: "Timeout",
      timeoutHint: "Maximum wait per request, in milliseconds.",
      maxRetries: "Max retries",
      maxRetriesHint: "Retries before failing over to another provider.",
      enabled: "Enabled",
      enabledHint: "A disabled provider is skipped by the router.",
    },
    detail: {
      title: "Provider details",
      back: "Back to providers",
      overview: "Overview",
      id: "Technical id",
      slug: "Slug",
      kind: "Kind",
      baseUrl: "Base URL",
      health: "Health",
      priority: "Priority",
      weight: "Weight",
      timeout: "Timeout",
      maxRetries: "Max retries",
      credentials: "Credentials",
      models: "Models",
      createdAt: "Created at",
      updatedAt: "Last change",
      description: "Description",
    },
    delete: {
      title: "Delete provider",
      message: "Delete “{{name}}”?",
      impact: "This removes {{credentials}} credentials and {{models}} models.",
      impactEndpoints: "{{endpoints}} model endpoints will stop being reachable.",
      confirm: "Delete provider",
      done: "Provider deleted",
    },
    created: "Provider created",
    updated: "Changes saved",
    enabledDone: "Provider enabled",
    disabledDone: "Provider disabled",
    notFound: "This provider does not exist.",
  },

  credentials: {
    title: "Credentials",
    subtitle: "Store, verify and rotate the access keys of your providers.",
    selectProvider: "Provider",
    selectProviderHint: "Pick a provider to manage its credentials.",
    noProviders: "Add a provider before managing credentials.",
    addCredential: "Add credential",
    createTitle: "Add credential",
    createSubtitle: "The access key is stored encrypted and is never displayed again.",
    empty: "No credential has been stored for this provider",
    emptyHint: "Connection tests and model discovery stay unavailable until a key is stored.",
    table: {
      label: "Label",
      status: "Status",
      key: "Key",
      lastVerified: "Last check",
      lastError: "Last error",
      createdAt: "Created at",
    },
    label: "Label",
    labelHint: "A name that identifies this key, for example “Primary key”.",
    secret: "Access key",
    secretHint: "The key is only accepted on creation and is stored encrypted.",
    secretPlaceholder: "sk-…",
    secretKeepHint: "Stored keys are never read back; use “Rotate key” to replace one.",
    verify: "Verify",
    verifying: "Verifying…",
    verifyOk: "The key is valid.",
    verifyFailed: "The key is not valid.",
    rotate: "Rotate key",
    rotateTitle: "Rotate key",
    rotateSubtitle: "The new key replaces the current one and the status returns to “unverified”.",
    newSecret: "New key",
    revoke: "Revoke",
    revokeConfirm: "Revoke this key? Until a new key is stored the provider cannot be routed to.",
    revokeMessage: "Revoke the key “{{label}}”?",
    delete: {
      title: "Delete credential",
      message: "Delete “{{label}}”?",
      boundEndpoints: "{{count}} model endpoints depend on this key and will fall back to the provider default.",
      confirm: "Delete key",
      done: "Credential deleted",
    },
    defaultCredential: "Provider default key",
    noDefaultCredential: "No default key is configured",
    created: "Credential created",
    updated: "Changes saved",
    rotated: "Key rotated",
    verified: "Verification finished",
    revoked: "Key revoked",
    statusUnverified: "Run “Verify” after every key change to confirm it still works.",
  },

  models: {
      title: "Models",
      subtitle: "Manage and test the models each provider exposes.",
      addModel: "Add model",
      createTitle: "Add model",
      createSubtitle: "Register a model the provider serves manually.",
      editTitle: "Edit model",
      editSubtitle: "Change pricing, context window and state.",
      empty: "No model has been registered yet",
      emptyHint: "Discover them from a provider or add one manually.",
      searchPlaceholder: "Search by model name…",
      filterProvider: "Provider",
      allProviders: "All providers",
      filterState: "State",
      allStates: "All states",
      stateEnabled: "Enabled",
      stateDisabled: "Disabled",
      stateDeprecated: "Deprecated",
      orderBy: "Order",
      sortName: "Name",
      sortProvider: "Provider",
      sortNewest: "Newest",
      clearFilters: "Clear filters",
      discover: {
        action: "Discover models",
        title: "Discover models",
        subtitle: "The provider catalogue is read and reconciled with the current registry.",
        provider: "Provider",
        overwrite: "Update existing models",
        overwriteHint: "When enabled, existing rows are refreshed with the provider values.",
        run: "Start discovery",
        running: "Discovering models…",
        resultTitle: "Discovery result",
        discovered: "Discovered",
        created: "New",
        updated: "Updated",
        skipped: "Unchanged",
        failed: "Failed",
        done: "Discovery finished",
        needProvider: "Add a provider with credentials before discovering models.",
      },
      table: {
        name: "Model",
        provider: "Provider",
        capabilities: "Capabilities",
        contextWindow: "Context window",
        pricing: "Pricing",
        state: "State",
        discoveredAt: "Discovered",
        actions: "Actions",
      },
      form: {
        name: "Provider model name",
        nameHint: "The identifier used in requests, e.g. gpt-4o-mini",
        displayName: "Display name",
        displayNameHint: "The name shown in the panel.",
        provider: "Provider",
        contextWindow: "Context window",
        contextWindowHint: "Maximum number of tokens the model accepts.",
        maxOutputTokens: "Max output tokens",
        inputPrice: "Input price (per 1M tokens)",
        outputPrice: "Output price (per 1M tokens)",
        pricingHint: "Prices are stored in USD per million tokens; leave empty when unknown.",
        capabilities: "Capabilities",
        capabilitiesHint: "Comma separated, e.g. vision, tools",
        enabled: "Enabled",
        enabledHint: "A disabled model is unavailable to routing and the gateway.",
        deprecated: "Deprecated",
        deprecatedHint: "Deprecated models are flagged and never selected by default.",
        sectionIdentity: "Model identity",
        sectionPricing: "Pricing",
        sectionState: "State",
      },
      detail: {
        title: "Model details",
        back: "Back to models",
        specs: "Specification",
        endpoints: "Model endpoints",
        playground: "Test model",
        provider: "Provider",
        contextWindow: "Context window",
        maxOutputTokens: "Max output tokens",
        inputPrice: "Input price",
        outputPrice: "Output price",
        capabilities: "Capabilities",
        discoveredAt: "Discovered",
        createdAt: "Created at",
        updatedAt: "Last change",
        id: "Technical id",
        noCapabilities: "No capability was recorded",
        priceUnknown: "Unknown",
        pricePerMillion: "${{price}} per 1M tokens",
      },
      endpoints: {
        empty: "No endpoint has been registered for this model",
        emptyHint: "The endpoint decides the path and method the model is called with.",
        add: "Add endpoint",
        createTitle: "Add model endpoint",
        editTitle: "Edit model endpoint",
        path: "Path",
        pathHint: "Path relative to the provider base URL, e.g. /chat/completions",
        method: "Method",
        streaming: "Streaming",
        streamingHint: "Does this endpoint answer with a stream?",
        credential: "Credential",
        credentialHint: "When selected, requests use this key; otherwise the provider default is used.",
        credentialDefault: "Provider default key",
        enabled: "Enabled",
        table: {
          path: "Path",
          method: "Method",
          streaming: "Streaming",
          credential: "Key",
          state: "State",
          actions: "Actions",
        },
        created: "Endpoint created",
        updated: "Changes saved",
        deleteTitle: "Delete model endpoint",
        deleteMessage: "Delete the endpoint “{{path}}”?",
        deleteConfirm: "Delete endpoint",
        deleted: "Endpoint deleted",
      },
      playground: {
        title: "Test model",
        subtitle: "Send a real request and inspect the answer, latency and token usage.",
        prompt: "Prompt",
        promptPlaceholder: "Write your question or instruction…",
        temperature: "Temperature",
        temperatureHint: "Lower values are more deterministic, higher values more creative.",
        maxTokens: "Max output tokens",
        stream: "Streaming answer",
        run: "Send request",
        running: "Running…",
        streaming: "Receiving the answer…",
        output: "Model answer",
        outputEmpty: "No answer was received.",
        latency: "Latency",
        inputTokens: "Input tokens",
        outputTokens: "Output tokens",
        totalTokens: "Total tokens",
        finishReason: "Finish reason",
        errorTitle: "The request failed",
        streamUnsupported: "This model does not support streaming; the full answer was requested instead.",
        needPrompt: "Enter a prompt before sending the request.",
        cost: "Estimated cost",
        costHint: "Computed from the recorded model pricing; hidden when no price is stored.",
        recentPrompt: "Previous prompt",
      },
      created: "Model created",
      updated: "Changes saved",
      enabledDone: "Model enabled",
      disabledDone: "Model disabled",
      deprecatedDone: "Model deprecated",
      restoredDone: "Model restored from deprecation",
      delete: {
        title: "Delete model",
        message: "Delete the model “{{name}}”?",
        impact: "Its model endpoints are removed as well.",
        confirm: "Delete model",
        done: "Model deleted",
      },
  },

  apiKeys: {
    title: "API keys",
    subtitle: "Issue, limit and monitor the access keys your consumers use.",
    addKey: "Issue key",
    createTitle: "Issue an API key",
    createSubtitle: "The key is shown once after creation. Store it somewhere safe.",
    editTitle: "Edit key",
    editSubtitle: "Change the name, scopes and limits. The key value itself never changes.",
    empty: "No API key has been issued yet",
    emptyHint: "Issue the first key so a consumer service can reach the gateway.",
    searchPlaceholder: "Search by name or key prefix…",
    filterState: "State",
    allStates: "All states",
    stateActive: "Active",
    stateDisabled: "Disabled",
    stateRevoked: "Revoked",
    stateExpired: "Expired",
    table: {
      name: "Name",
      key: "Key",
      scopes: "Scopes",
      rateLimit: "Rate limit",
      quota: "Quota",
      lastUsed: "Last used",
      expiresAt: "Expires",
      state: "State",
      createdAt: "Issued",
      actions: "Actions",
    },
    scope: { chat: "Chat", embeddings: "Embeddings", models: "Models" },
    form: {
      name: "Name",
      nameHint: "The name shown in the key list.",
      scopes: "Scopes",
      scopesHint: "Each scope unlocks one part of the gateway.",
      rateLimit: "Rate limit (per minute)",
      rateLimitHint: "Maximum requests per minute for this key.",
      quota: "Token quota",
      quotaHint: "Optional; once consumed, the key receives 429 responses.",
      expiresAt: "Expiry date",
      expiresAtHint: "Optional; the key stops working after this date.",
      enabled: "Enabled",
    },
    secret: {
      title: "Key created",
      notice: "This key is displayed only once. After this dialog closes the value cannot be retrieved.",
      label: "API key",
      copied: "Key copied",
      done: "I saved the key",
    },
    usage: {
      title: "Key usage",
      sampleMessage: "Hello",
      requests: "Requests",
      inputTokens: "Input tokens",
      outputTokens: "Output tokens",
      totalTokens: "Total tokens",
      cost: "Cost",
      errors: "Errors",
      quota: "Quota consumed",
      remaining: "Quota remaining",
      noQuota: "No quota is set for this key",
    },
    actions: { usage: "View usage", revoke: "Revoke", enable: "Enable", disable: "Disable" },
    revoke: {
      title: "Revoke key",
      message: "Revoke “{{name}}”?",
      hint: "After revocation requests made with this key receive 401, but its usage history is kept.",
      confirm: "Revoke key",
      done: "Key revoked",
    },
    delete: {
      title: "Delete key",
      message: "Delete “{{name}}”?",
      hint: "Deleting removes its usage history too. Revoking is the better way to stop access.",
      confirm: "Delete key",
      done: "Key deleted",
    },
    created: "Key issued",
    updated: "Key changes saved",
    enabledDone: "Key enabled",
    disabledDone: "Key disabled",
    revokedNote: "This key has been revoked and no longer works.",
    expiredNote: "This key has passed its expiry date.",
    intro: {
      title: "How to use it",
      baseUrl: "Gateway base URL",
      hint: "Send the key in the Authorization header using the Bearer scheme. Routes live under /v1.",
      endpoints: "Available routes",
    },
  },
    api_key_not_found: "The API key does not exist.",
    unknown_api_key_scope: "The selected scope is not supported.",
    api_key_limit_reached: "The maximum number of API keys has been reached.",
    invalid_api_key: "The API key is invalid, revoked or expired.",
    gateway_disabled: "The gateway is disabled on this deployment.",
    quota_exceeded: "This key exhausted its token quota.",
    insufficient_scope: "This key may not use this capability.",
    gateway_deadline_exceeded: "The request deadline was reached.",
    gateway_no_streaming_candidate: "No provider can serve this model as a stream.",
    invalid_request: "The request is invalid.",

  routing: {
    title: "Smart routing",
    subtitle: "Choose how a provider is selected and dry-run the outcome before applying it.",
    strategiesTitle: "Strategies",
    strategiesHint: "The strategy for a model comes from the rules below; without a rule, priority applies.",
    implemented: "Implemented",
    columns: {
      strategy: "Strategy",
      needs: "Required inputs",
      uses: "Decision criteria",
      summary: "Summary",
    },
    needs: { priority: "Provider priority", weight: "Provider weight", none: "No special input" },
    uses: { health: "Health", latency: "Latency", cost: "Cost", order: "Fixed order" },
    rules: {
      title: "Routing rules",
      empty: "No routing rule yet",
      emptyHint: "Add a rule to set a strategy and fallback chain for a model or a name pattern.",
      add: "Add rule",
      createTitle: "Add routing rule",
      editTitle: "Edit rule",
      createSubtitle: "A rule only affects requests that match its conditions.",
      editSubtitle: "Changes apply to subsequent requests.",
      name: "Rule name",
      modelPattern: "Model name pattern",
      modelPatternHint: "For example gpt-4o* — leave empty to apply to every model.",
      strategy: "Strategy",
      priority: "Rule priority",
      priorityHint: "Lower numbers are evaluated first; the first matching rule wins.",
      enabled: "Rule enabled",
      table: { name: "Name", pattern: "Condition", strategy: "Strategy", priority: "Priority", enabled: "State", actions: "Actions" },
      allModels: "All models",
      delete: {
        title: "Delete rule",
        message: "Delete the rule “{{name}}”?",
        confirm: "Delete rule",
        done: "Rule deleted",
      },
      created: "Rule created",
      updated: "Rule changes saved",
      deleted: "Rule deleted",
      enabledDone: "Rule enabled",
      disabledDone: "Rule disabled",
    },
    simulate: {
      title: "Routing simulation",
      subtitle: "Nothing is sent upstream; only the selection order and the reasoning are shown.",
      model: "Model",
      modelPlaceholder: "Exact model name, e.g. gpt-4o-mini",
      strategy: "Simulation strategy",
      useRule: "Matching rule strategy",
      tokens: "Estimated tokens",
      tokensHint: "Used for the cost estimate only; no tokens are consumed.",
      run: "Run simulation",
      resultTitle: "Selection order",
      selected: "First choice",
      eligible: "Eligible",
      excluded: "Excluded",
      candidate: "Candidate",
      score: "Score",
      latency: "Observed latency",
      price: "Price per 1M tokens",
      estimatedCost: "Estimated cost",
      reasons: "Reasons",
      noCandidates: "No enabled provider serves this model",
      noCandidatesHint: "Register the model and store the provider credential first.",
      explanation: "Decision details",
      empty: "Simulate a model to see the selection order",
      emptyHint: "Enter a model name and press “Run simulation”.",
      failed: "Simulation failed",
    },
    reason: {
      heading: "Reasons",
      provider_priority: "Provider priority: {{value}}",
      weight: "Provider weight: {{value}}",
      health: "Health: {{value}}",
      observed_latency_ms: "Observed latency: {{value}} ms",
      no_latency_sample: "No latency sample recorded yet",
      blended_price: "Cost per 1M input + output tokens: ${{value}}",
      no_price_data: "No price recorded for this model",
      excluded_health_down: "Excluded because the provider is down",
      round_robin_turn: "This candidate holds the turn",
      round_robin_offset: "Rotation position: {{value}}",
      unknown: "{{value}}",
    },
  },

  benchmark: {
    title: "Benchmark",
    subtitle: "Measured model performance from real traffic",
    range: "Time range",
    days7: "7 days",
    days14: "14 days",
    days30: "30 days",
    days90: "90 days",
    kpiModels: "Models evaluated",
    kpiRequests: "Total requests",
    kpiSuccess: "Success rate",
    kpiLatency: "Avg latency",
    kpiRequestsHint: "Requests across all models in this range",
    kpiSuccessHint: "Weighted by each model's request count",
    kpiLatencyHint: "Weighted by each model's request count",
    chartScores: "Model scores",
    chartScoresHint: "Composite performance score, 0 to 1",
    chartScoresEmpty: "No score computed for these models",
    trend: "Trend over time",
    trendHint: "Daily, for the top {count} models by requests",
    metricRequests: "Requests",
    metricLatency: "Latency (ms)",
    trendEmpty: "No daily data for this range",
    trendMoreHidden: "{hidden} more models appear only in the table below",
    unitMs: "ms",
    tableTitle: "Model details",
    tableHint: "Click a column header to sort",
    colModel: "Model",
    colProvider: "Provider",
    colScore: "Score",
    colSuccess: "Success",
    colLatency: "Avg latency",
    colRequests: "Requests",
    emptyTitle: "No benchmark data yet",
    emptyHint: "Scores appear here once models have served real requests",
  },

  enums: {
    modelState: { enabled: "Enabled", disabled: "Disabled", deprecated: "Deprecated" },
    apiKeyState: { active: "Active", disabled: "Disabled", revoked: "Revoked", expired: "Expired" },
    providerKind: { openai: "OpenAI", anthropic: "Claude", google: "Gemini", deepseek: "DeepSeek", qwen: "Qwen", openai_compatible: "OpenAI-compatible" },
    credentialStatus: { unverified: "Unverified", active: "Active", invalid: "Invalid", revoked: "Revoked" },
    healthStatus: {
      healthy: "Healthy",
      degraded: "Degraded",
      rate_limited: "Rate limited",
      down: "Down",
      unknown: "Unknown",
    },
    clientRequestState: { pending: "Pending", succeeded: "Succeeded", failed: "Failed" },
    strategy: { priority: "Priority", weighted: "Weighted", round_robin: "Round robin", latency_aware: "Latency aware", cost_aware: "Cost aware", failover: "Failover" },
    role: { owner: "Owner", admin: "Admin", operator: "Operator", viewer: "Viewer" },
    userStatus: { active: "Active", suspended: "Suspended", invited: "Invited" },
    environment: { development: "Development", test: "Test", staging: "Staging", production: "Production" },
    capabilityState: { implemented: "Implemented", placeholder: "Placeholder", planned: "Planned" },
  },

  errors: {
    title: "Something went wrong",
    generic: "The operation could not be completed. Please try again.",
    network: "The server could not be reached. Check your network connection.",
    timeout: "The server did not respond in time.",
    invalid_credentials: "The email or password is incorrect.",
    authentication_required: "You need to sign in to continue.",
    authentication_failed: "Authentication failed.",
    token_expired: "Your session has expired. Sign in again.",
    token_invalid: "The access token is invalid.",
    token_type_invalid: "This token type is not accepted here.",
    token_revoked: "This session has been revoked. Sign in again.",
    refresh_token_missing: "No active session found. Please sign in.",
    account_inactive: "This account is inactive. Contact the platform owner.",
    account_missing: "The account no longer exists.",
    insufficient_role: "Your role does not allow this action.",
    permission_denied: "You do not have access to this section.",
    bootstrap_closed: "Initial setup has already been completed.",
    bootstrap_disabled: "Initial setup is disabled on this deployment.",
    role_escalation_not_allowed: "Only an owner can grant the owner role.",
    cannot_change_own_role: "You cannot change your own role.",
    email_taken: "An account with this email already exists.",
    owner_role_protected: "The owner role cannot be changed.",
    cannot_suspend_self: "You cannot deactivate your own account.",
    password_too_short: "The password must be at least {{min_length}} characters long.",
    unknown_setting: "This setting is not recognised.",
    invalid_setting_value: "The submitted value is not valid for this setting.",
    setting_read_only: "This setting cannot be changed from the panel.",
    login_rate_limited: "Too many sign-in attempts. Try again later.",
    rate_limit_exceeded: "Too many requests. Try again shortly.",
    request_validation_failed: "Some fields are invalid. Review the values.",
    validation_failed: "The submitted data is invalid.",
    not_found: "No record matches this identifier.",
    route_not_found: "This route does not exist in the service.",
    conflict: "The operation conflicts with the current state.",
    internal_error: "An unexpected service error occurred.",
    dependency_unavailable: "A required dependency is unavailable.",
    component_down: "A required platform component is down.",
    not_implemented: "This capability ships in a later milestone.",
    method_not_allowed: "This method is not allowed on this route.",
    http_error: "The service returned an unsuccessful response.",
    unknown_error: "An unknown error occurred.",

      model_not_found: "No model matches this identifier.",
      model_name_duplicate: "This provider already exposes a model with that name.",
      endpoint_not_found: "No model endpoint matches this identifier.",
      endpoint_path_invalid: "The endpoint path must start with “/”.",
      endpoint_of_another_model: "This endpoint belongs to a different model.",
      model_disabled: "This model is disabled.",
    provider_not_found: "No provider matches this identifier.",
    credential_not_found: "No credential matches this identifier.",
    credential_label_duplicate: "A credential with this label already exists for the provider.",
    credential_decryption_failed: "The stored key could not be read. Check the deployment encryption key.",
    base_url_required: "This provider kind requires a base URL.",
    credential_missing: "No credential has been stored for this provider.",
    provider_error: "The provider answered with an unsuccessful response.",
    provider_unauthorized: "The access key was rejected (HTTP 401).",
    provider_forbidden: "This key may not perform the operation (HTTP 403).",
    provider_rate_limited: "The provider is rate limiting requests (HTTP 429). Try again shortly.",
    provider_timeout: "The provider did not answer within the configured timeout.",
    provider_unavailable: "The provider service is temporarily unavailable.",
    provider_unreachable: "The network connection to the provider failed.",
    provider_bad_request: "The request sent to the provider was invalid.",
    provider_request_too_large: "The request exceeds the provider payload limit.",
    provider_unprocessable: "The provider rejected the request payload (HTTP 422).",
    provider_model_not_found: "The provider does not expose this model.",
    provider_unexpected_response: "The provider response did not match the expected shape.",
    provider_base_url_missing: "The provider has no base URL configured.",
    provider_discovery_unsupported: "This provider does not support automatic model discovery.",
    provider_chat_unsupported: "This provider does not support chat completions.",
    provider_streaming_unsupported: "This provider does not support streaming responses.",
    provider_embeddings_unsupported: "This provider does not support embeddings.",
    requestId: "Request id",
    technicalDetails: "Technical details",
    errorCode: "Error code",
    retry: "Retry",
    returnToDashboard: "Back to dashboard",
  },

  validation: {
    required: "This field is required.",
    email: "Enter a valid email address.",
    minLength: "This value must be at least {{count}} characters long.",
    maxLength: "This value must be at most {{count}} characters long.",
    number: "Enter a valid number.",
    positiveNumber: "Enter a positive number.",
    url: "Enter a valid URL starting with http or https.",
    password: "The password must be at least 10 characters long.",
  },

  usage: {
    title: "Usage",
    subtitle:
      "Inspect requests, tokens, cost, latency and error rate based on the attempts actually sent upstream.",
    filters: {
      title: "Date range",
      datePlaceholder: "1405/06/28",
      from: "From",
      to: "To",
      hint: "Dates are Jalali — for example 1405/06/28. Leaving the range empty shows the last seven days.",
      invalidDate: "Enter a Jalali date such as 1405/06/28.",
      presetToday: "Today",
      preset7: "Last 7 days",
      preset30: "Last 30 days",
      preset90: "Last 90 days",
      provider: "Provider",
      model: "Model",
      apiKey: "API key",
      all: "All",
      apply: "Apply filters",
      reset: "Clear filters",
      activeRange: "Selected range: {{range}}",
      interval: "Granularity",
      intervalHour: "Hourly",
      intervalDay: "Daily",
      intervalWeek: "Weekly",
      intervalMonth: "Monthly",
    },
    totals: {
      requests: "Requests",
      requestsHint: "Attempts recorded in this range",
      tokens: "Total tokens",
      tokensHint: "Input and output combined",
      cost: "Cost",
      costHint: "Based on each model's pricing",
      latency: "Average latency",
      p95: "95th percentile latency",
      errorRate: "Error rate",
      errors: "Errors",
      errorsHint: "Failed attempts",
      inputTokens: "Input tokens",
      outputTokens: "Output tokens",
    },
    breakdown: {
      title: "Usage breakdown",
      subtitle: "Compare consumption by provider, model or API key.",
      dimension: "Group by",
      byProvider: "Provider",
      byModel: "Model",
      byKey: "API key",
      label: "Label",
      requests: "Requests",
      tokens: "Tokens",
      cost: "Cost",
      errors: "Errors",
      errorRate: "Error rate",
      latency: "Avg latency",
      empty: "No usage recorded for this range",
      emptyHint: "Try another date range or clear the filters.",
    },
    series: {
      title: "Usage trend",
      subtitle: "Each row is one interval inside the selected range.",
      bucket: "Interval",
      requests: "Requests",
      tokens: "Tokens",
      cost: "Cost",
      errors: "Errors",
      latency: "Avg latency",
      empty: "Nothing to show for this range",
    },
    export: {
      csv: "Export CSV",
      json: "Export JSON",
      csvHint: "The CSV file is written as UTF-8 with a BOM so Excel opens Persian text correctly.",
      jsonHint: "The JSON file suits automation and scripts.",
      done: "File “{{name}}” is ready",
      failed: "Export failed",
      rollups: "Rebuild daily rollups",
      rollupsHint: "Recomputes the daily rollup from the stored attempts.",
      rollupsDone: "Daily rollups rebuilt",
    },
    states: {
      noActivity: "No traffic recorded for this range",
      noActivityHint: "Usage statistics appear here after the first successful gateway request.",
      rangeNote: "Data range: {{range}}",
      generatedAt: "Computed at {{time}}",
    },
  },

  logs: {
    title: "Request logs",
    subtitle: "Inspect every gateway request with its outcome, attempts, tokens and latency.",
    filters: {
      title: "Filters",
      datePlaceholder: "1405/06/28",
      search: "Search",
      searchPlaceholder: "Request id, provider, model or error code",
      state: "State",
      allStates: "All states",
      errorCode: "Error code",
      errorCodePlaceholder: "e.g. provider_unavailable",
      dateFrom: "From",
      dateTo: "To",
      dateHint: "Jalali date (1405/06/28); empty means no limit.",
      invalidDate: "Enter a Jalali date such as 1405/06/28.",
      provider: "Provider",
      model: "Model",
      all: "All",
      apply: "Apply filters",
      reset: "Clear filters",
    },
    table: {
      time: "Time",
      requestId: "Request id",
      key: "API key",
      provider: "Provider",
      model: "Model",
      state: "State",
      attempts: "Attempts",
      statusCode: "Status code",
      tokens: "Tokens",
      latency: "Latency",
      details: "Details",
    },
    state: {
      succeeded: "Succeeded",
      failed: "Failed",
      pending: "Pending",
    },
    streaming: "Streaming",
    attemptsCount: "{{count}} attempts",
    empty: "No request matches these filters",
    emptyHint: "Change the date range, state or search text.",
    pagination: {
      previous: "Previous",
      next: "Next",
      page: "Page {{page}} of {{total}}",
      total: "{{count}} requests",
    },
    detail: {
      title: "Request detail",
      subtitle: "Id {{requestId}}",
      failureOnly: "Only failed requests have details?",
      summary: "Request summary",
      state: "State",
      statusCode: "Status code",
      errorCode: "Error code",
      attempts: "Upstream attempts",
      attemptsHint:
        "Each attempt is a real provider call; the order shows what failover actually did.",
      attempt: "Attempt {{number}}",
      finalAttempt: "Final attempt",
      retryableAttempt: "Retryable",
      provider: "Provider",
      model: "Model",
      credential: "Credential",
      endpoint: "Endpoint",
      tokens: "Tokens (input/output)",
      cost: "Cost",
      latency: "Latency",
      raw: "Raw payload (JSON)",
      rawHint: "For technical reference and bug reports; always rendered left-to-right.",
      copy: "Copy JSON",
      copied: "Copied",
      noAttempts: "No attempt recorded for this request",
      loadFailed: "Could not load the request detail",
    },
  },

  tools: {
    title: "Tools",
    subtitle: "Manage gateway tool definitions and permissions.",
    addTool: "Add tool",
    createTitle: "Register new tool",
    listTitle: "Registered tools",
    empty: "No tools registered",
    emptyHint: "Register a tool to enable gateway-native tool orchestration.",
    createDone: "Tool created",
    createError: "Could not create tool",
    toggleDone: "Tool updated",
    deleteDone: "Tool deleted",
    confirmDelete: "Delete tool \"{{name}}\"?",
    table: { name: "Name", type: "Type", policy: "Policy", timeout: "Timeout", status: "Status", actions: "Actions" },
    form: { name: "Tool name", description: "Description", schema: "Input schema (JSON)" },
  },

  toolExecutions: {
    title: "Tool executions",
    subtitle: "Audit trail of every tool invocation through the gateway.",
    listTitle: "Recent executions",
    empty: "No tool executions yet",
    emptyHint: "Tool executions appear here after the first tool call through the gateway.",
    table: { time: "Time", tool: "Tool", controller: "Controller", status: "Status", latency: "Latency", iteration: "Iter", requestId: "Request" },
  },

  notFound: {
    title: "Page not found",
    description: "The requested route does not exist in the admin panel.",
    action: "Back to dashboard",
  },

  states: {
    loadingTitle: "Loading data",
    errorTitle: "Could not load data",
    emptyTitle: "Nothing to display",
    retry: "Retry",
  },
};

export default en;
